import useRoomCapacityText from '@common/hooks/useRoomCapacityText';
import { useFocusPageHeading } from '@common/hooks/useFocusPageHeading';
import { ContentParser } from '@common/components/ContentParser';
import { analytics } from '@features/analytics';
import { useTranslations } from '@lang';
import { FC } from 'react';
import { useRouter } from 'shared-routing';
import { RoomUpgradeTemplate } from 'voyager';
import { UpgradeItemV2Props } from './UpgradeItemV2.types';

export const UpgradeItemV2: FC<UpgradeItemV2Props> = ({
  isOpen,
  id,
  imageUrl,
  name,
  description,
  amenities,
  priceDifference,
  pricePerStay,
  pricePerNight,
  quantity,
  displayUrgencyMessaging,
  maxOccupancy,
  maxAdults,
  maxChildren,
  beds,
  linkRoute,
  linkParams,
  upgradeLinkParams,
  originalRoom,
  trackingItem,
  language,
  numberOfNights,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const capacityText = useRoomCapacityText({ maxAdults, maxChildren, maxOccupancy });

  const priceDifferencePerNight =
    priceDifference && numberOfNights && numberOfNights > 0
      ? { ...priceDifference, amount: priceDifference.amount / numberOfNights }
      : undefined;

  useFocusPageHeading(isOpen ? 'upgrade' : undefined);

  if (!isOpen) return null;

  const handleSelection = (accepted: boolean) => {
    analytics.track({
      event: 'upgrade_selection',
      params: {
        items: [
          {
            original_item_name: originalRoom?.name ?? trackingItem?.name ?? 'unknown',
            original_item_id: originalRoom?.id ?? trackingItem?.id ?? 'unknown',
            item_name: name,
            item_id: id,
            price_difference: priceDifference,
            price_per_stay: pricePerStay,
            accepted,
          },
        ],
      },
    });
    router.push(
      { pathname: linkRoute, query: accepted ? upgradeLinkParams : linkParams },
      undefined,
      {
        shallow: !accepted,
      }
    );
  };

  const bedsText = beds?.types?.map((bed) => `${bed.quantity} ${bed.name}`).join(' • ');

  return (
    <RoomUpgradeTemplate
      locale={language}
      originalRoom={
        originalRoom?.name
          ? { name: originalRoom.name, imageUrl: originalRoom.imageUrl }
          : undefined
      }
      upgradeRoom={{
        name,
        imageUrl,
        capacityText,
        bedsText,
        urgencyText:
          displayUrgencyMessaging && quantity ? t('room.hurryMessage', { quantity }) : undefined,
        description: description ? (
          <ContentParser text={description} escapeHtml={false} />
        ) : undefined,
        features: amenities ?? undefined,
        priceDifference,
        priceDifferencePerNight,
        pricePerStay,
        pricePerNight,
      }}
      onBack={() => router.back()}
      onSkip={() => handleSelection(false)}
      onUpgrade={() => handleSelection(true)}
      labels={{
        title: t('room.upgrade.title'),
        titleAccent: t('room.upgrade.subtitle'),
        backAriaLabel: t('common.back'),
        seeDetails: t('room.upgrade.showDetails'),
        upgradeFor: t('room.upgrade.upgradeFor'),
        forStay: t('room.upgrade.forStay'),
        perNight: t('room.night'),
        totalDue: t('room.upgrade.totalDue'),
        skip: t('room.upgrade.noThanks'),
        upgrade: t('room.upgrade'),
        close: t('room.upgrade.close'),
        noImage: t('basket.noImageLabel'),
        previousImage: t('product.previousImage'),
        nextImage: t('product.nextImage'),
        goToImage: (position, total) => t('product.viewImage', { position, total }),
      }}
    />
  );
};
